<template>
  <div class="products-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="products-container">
          <!-- Setup Steps -->
          <div class="setup-steps">
            <div class="step-card">
              <div class="step-number">Step 01</div>
              <div class="step-content">
                <h3 class="step-title">Download Sample</h3>
                <p class="step-description">Download the Excel template with the required format</p>
                <a href="https://api.quali-d.com/QUALI-D_Products_Services_Sample.xlsx" download class="btn-download">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M12,19L8,15H10.5V12H13.5V15H16M13,9V3.5L18.5,9H13Z"/>
                  </svg>
                  Download Sample
                </a>
              </div>
            </div>

            <div class="step-card">
              <div class="step-number">Step 02</div>
              <div class="step-content">
                <h3 class="step-title">Fill Your Data</h3>
                <p class="step-description">Add your products/services with descriptions. Include pricing details in the description if applicable</p>
              </div>
            </div>

            <div class="step-card">
              <div class="step-number">Step 03</div>
              <div class="step-content">
                <h3 class="step-title">Upload File</h3>
                <p class="step-description">Upload the completed Excel file to import your data</p>
                <button @click="showUploadModal = true" class="btn-upload">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  Upload File
                </button>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading products & services...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="error-icon">
              <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
            </svg>
            <h3>Error Loading Data</h3>
            <p>{{ error }}</p>
            <button @click="refreshData" class="btn-primary">Try Again</button>
          </div>

          <!-- Action Bar with Search, Add, and Delete -->
          <div v-if="!loading && !error" class="action-bar">
            <div v-if="products.length > 0" class="search-bar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search services and products..."
                class="search-input"
              >
              <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                </svg>
              </button>
            </div>

            <div class="action-buttons-group" :class="{ 'full-width': products.length === 0 }">
              <button @click="showAddSingleModal = true" class="btn-add">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                </svg>
                Add Product/Service
              </button>
              <button
                v-if="products.length > 0"
                @click="showDeleteModal = true"
                class="btn-delete-all"
                :disabled="products.length === 0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                </svg>
                Delete All
              </button>
            </div>
          </div>

          <!-- Data Display -->
          <div v-if="!loading && !error" class="content-container">
            <div v-if="products.length === 0" class="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
                <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
              </svg>
              <h3>No Data Found</h3>
              <p>No products or services have been uploaded yet.</p>
            </div>

            <!-- Cards Layout -->
            <div v-else class="cards-container">
              <!-- Results Info -->
              <div class="results-info">
                <span>{{ filteredProducts.length }} of {{ products.length }} services</span>
                <div class="view-toggle">
                  <button
                    @click="viewMode = 'cards'"
                    :class="{ active: viewMode === 'cards' }"
                    class="view-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,11H11V3H3M3,21H11V13H3M13,21H21V13H13M13,3V11H21V3"/>
                    </svg>
                    Cards
                  </button>
                  <button
                    @click="viewMode = 'list'"
                    :class="{ active: viewMode === 'list' }"
                    class="view-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,5H21V3H3M3,13H21V11H3M3,21H21V19H3"/>
                    </svg>
                    List
                  </button>
                </div>
              </div>

              <!-- Cards View -->
              <div v-if="viewMode === 'cards'" class="cards-grid">
                <div v-for="product in paginatedFilteredProducts" :key="product._id" class="product-card">
                  <div class="card-header">
                    <div class="card-header-content">
                      <h3 class="service-name">{{ getProductName(product) }}</h3>
                      <span class="upload-date">Date Added: {{ formatDate(product._created_at) }}</span>
                    </div>
                    <div class="card-actions">
                      <button @click="openAIEnhanceModal(product)" class="ai-enhance-btn" title="AI Enhance Product/Service">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/>
                        </svg>
                        <span>AI Enhance Product/Service</span>
                      </button>
                      <button @click="openEditModal(product)" class="edit-product-btn" title="Edit">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                        </svg>
                      </button>
                      <button @click="confirmDeleteProduct(product)" class="delete-product-btn" title="Delete">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div class="card-content">
                    <div v-for="header in getProductHeaders(product)" :key="header" class="field-group">
                      <template v-if="product[header] && product[header].trim()">
                        <label class="field-label">{{ formatHeader(header) }}</label>
                        <div class="field-content" v-html="formatContent(product[header])"></div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- List View -->
              <div v-else class="list-view">
                <div v-for="product in paginatedFilteredProducts" :key="product._id" class="product-item">
                  <div class="item-header">
                    <div class="item-title" @click="toggleExpand(product._id)">
                      <h3>{{ getProductName(product) }}</h3>
                      <span class="upload-date">Date Added: {{ formatDate(product._created_at) }}</span>
                    </div>
                    <div class="item-actions">
                      <button @click.stop="openAIEnhanceModal(product)" class="ai-enhance-btn" title="AI Enhance Product/Service">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/>
                        </svg>
                        <span>AI Enhance Product/Service</span>
                      </button>
                      <button @click.stop="openEditModal(product)" class="edit-product-btn" title="Edit">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                        </svg>
                      </button>
                      <button @click.stop="confirmDeleteProduct(product)" class="delete-product-btn" title="Delete">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                        </svg>
                      </button>
                      <svg
                        @click="toggleExpand(product._id)"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        :class="{ rotated: expandedItems.includes(product._id) }"
                        class="expand-icon"
                      >
                        <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"/>
                      </svg>
                    </div>
                  </div>

                  <div v-if="expandedItems.includes(product._id)" class="item-content">
                    <div v-for="header in getProductHeaders(product)" :key="header" class="field-group">
                      <template v-if="product[header] && product[header].trim()">
                        <label class="field-label">{{ formatHeader(header) }}</label>
                        <div class="field-content" v-html="formatContent(product[header])"></div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="totalFilteredPages > 1" class="pagination">
                <button
                  @click="currentPage = Math.max(1, currentPage - 1)"
                  :disabled="currentPage === 1"
                  class="pagination-btn"
                >
                  Previous
                </button>

                <span class="pagination-info">
                  Page {{ currentPage }} of {{ totalFilteredPages }} ({{ filteredProducts.length }} items)
                </span>

                <button
                  @click="currentPage = Math.min(totalFilteredPages, currentPage + 1)"
                  :disabled="currentPage === totalFilteredPages"
                  class="pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click="closeUploadModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Upload Products/Services</h3>
          <button @click="closeUploadModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Upload an Excel file (.xlsx, .xls) containing your products and services data.</p>
          <div class="upload-area">
            <input type="file" id="fileInput" @change="handleFileSelect" accept=".xlsx,.xls" hidden>
            <label for="fileInput" class="upload-label">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              <span>{{ selectedFile ? selectedFile.name : 'Choose Excel file' }}</span>
            </label>
          </div>
          <div v-if="uploadError" class="error-message">
            {{ uploadError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeUploadModal" class="btn-secondary">Cancel</button>
          <button @click="uploadFile" :disabled="!selectedFile || uploading" class="btn-primary">
            <div v-if="uploading" class="loading-spinner-small"></div>
            {{ uploading ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Single Product/Service Modal -->
    <div v-if="showAddSingleModal" class="modal-overlay" @click="closeAddSingleModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Add Product/Service</h3>
          <button @click="closeAddSingleModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="productName" class="form-label">Name *</label>
            <input
              id="productName"
              v-model="newProduct.name"
              type="text"
              placeholder="e.g., Website Development"
              class="form-input"
              :class="{ 'input-error': addErrors.name }"
            >
            <span v-if="addErrors.name" class="field-error">{{ addErrors.name[0] }}</span>
          </div>

          <div class="form-group">
            <label for="productDescription" class="form-label">Description *</label>
            <textarea
              id="productDescription"
              v-model="newProduct.description"
              placeholder="e.g., Custom responsive website development with modern design and SEO optimization. Includes pricing details if applicable."
              class="form-textarea"
              :class="{ 'input-error': addErrors.description }"
              rows="4"
            ></textarea>
            <span v-if="addErrors.description" class="field-error">{{ addErrors.description[0] }}</span>
          </div>

          <div v-if="addSingleError" class="error-message">
            {{ addSingleError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAddSingleModal" class="btn-secondary">Cancel</button>
          <button @click="addSingleProduct" :disabled="addingSingle" class="btn-primary">
            <div v-if="addingSingle" class="loading-spinner-small"></div>
            {{ addingSingle ? 'Adding...' : 'Add Product/Service' }}
          </button>
        </div>
      </div>
    </div>

    <!-- AI Enhancement Modal -->
    <div v-if="showAIEnhanceModal" class="modal-overlay" @click="closeAIEnhanceModal">
      <div class="modal modal-large" @click.stop>
        <div class="modal-header">
          <h3>AI Enhance Description</h3>
          <button @click="closeAIEnhanceModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="!aiEnhancedDescription">
            <p class="modal-description">Choose a style to enhance your product/service description with AI:</p>

            <!-- Original Description -->
            <div class="original-description-box">
              <label class="field-label">Original Description</label>
              <div class="original-description-text">{{ aiProductToEnhance ? (aiProductToEnhance.description || '') : '' }}</div>
            </div>

            <!-- Style Selection -->
            <div class="style-selection">
              <button
                @click="selectedStyle = 'professional'"
                :disabled="enhancingAI"
                class="style-btn professional"
                :class="{ 'selected': selectedStyle === 'professional' }"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"/>
                </svg>
                <span>Professional</span>
              </button>
              <button
                @click="selectedStyle = 'cool'"
                :disabled="enhancingAI"
                class="style-btn cool"
                :class="{ 'selected': selectedStyle === 'cool' }"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,17.5C14.33,17.5 16.3,16.04 17.11,14H6.89C7.69,16.04 9.67,17.5 12,17.5M8.5,11A1.5,1.5 0 0,0 10,9.5A1.5,1.5 0 0,0 8.5,8A1.5,1.5 0 0,0 7,9.5A1.5,1.5 0 0,0 8.5,11M15.5,11A1.5,1.5 0 0,0 17,9.5A1.5,1.5 0 0,0 15.5,8A1.5,1.5 0 0,0 14,9.5A1.5,1.5 0 0,0 15.5,11M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                <span>Cool</span>
              </button>
            </div>

            <!-- Start Enhancing Button -->
            <div v-if="selectedStyle && !enhancingAI" class="start-enhance-section">
              <div class="start-enhance-container">
                <button @click="enhanceWithAI(selectedStyle)" class="btn-start-enhance">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/>
                  </svg>
                  Start Enhancing
                </button>
                <span class="credits-cost">Cost 20 Credits</span>
              </div>
            </div>

            <div v-if="enhancingAI" class="loading-message">
              <div class="loading-spinner"></div>
              <p>AI is enhancing your description...</p>
            </div>

            <div v-if="aiEnhanceError" class="error-message">
              {{ aiEnhanceError }}
            </div>
          </div>

          <!-- Enhanced Description Editor -->
          <div v-else>
            <div class="enhancement-info">
              <span class="style-badge" :class="selectedStyle">{{ selectedStyle }}</span>
              <span class="credits-info">Credits used: {{ creditsUsed }} | Remaining: {{ remainingCredits }}</span>
            </div>

            <div class="form-group">
              <label class="field-label">Enhanced Description (Editable)</label>
              <textarea
                v-model="aiEnhancedDescription"
                class="form-textarea enhanced-textarea"
                :dir="enhancedDescriptionDirection"
                rows="8"
              ></textarea>
            </div>

            <div class="comparison-section">
              <label class="field-label">Original Description</label>
              <div class="original-description-text">{{ originalDescription }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAIEnhanceModal" class="btn-secondary">Cancel</button>
          <button
            v-if="aiEnhancedDescription"
            @click="updateProductDescription"
            :disabled="updatingProduct"
            class="btn-primary"
          >
            <div v-if="updatingProduct" class="loading-spinner-small"></div>
            {{ updatingProduct ? 'Saving...' : 'Save AI Enhanced Description' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Single Product Confirmation Modal -->
    <div v-if="showDeleteSingleModal" class="modal-overlay" @click="closeDeleteSingleModal">
      <div class="modal modal-small" @click.stop>
        <div class="modal-header">
          <h3>Delete Product/Service</h3>
          <button @click="closeDeleteSingleModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ productToDelete ? getProductName(productToDelete) : '' }}</strong>?</p>
          <p class="text-muted">This action cannot be undone.</p>
          <div v-if="deleteSingleError" class="error-message">
            {{ deleteSingleError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDeleteSingleModal" class="btn-secondary">Cancel</button>
          <button @click="deleteSingleProduct" :disabled="deletingSingle" class="btn-danger">
            <div v-if="deletingSingle" class="loading-spinner-small"></div>
            {{ deletingSingle ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Product/Service Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Edit Product/Service</h3>
          <button @click="closeEditModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="editProductName" class="form-label">Name *</label>
            <input
              id="editProductName"
              v-model="editProduct.name"
              type="text"
              placeholder="e.g., Website Development"
              class="form-input"
              :class="{ 'input-error': editErrors.name }"
            >
            <span v-if="editErrors.name" class="field-error">{{ editErrors.name[0] }}</span>
          </div>

          <div class="form-group">
            <label for="editProductDescription" class="form-label">Description *</label>
            <textarea
              id="editProductDescription"
              v-model="editProduct.description"
              placeholder="e.g., Custom responsive website development with modern design and SEO optimization. Includes pricing details if applicable."
              class="form-textarea"
              :class="{ 'input-error': editErrors.description }"
              rows="4"
            ></textarea>
            <span v-if="editErrors.description" class="field-error">{{ editErrors.description[0] }}</span>
          </div>

          <div v-if="editError" class="error-message">
            {{ editError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeEditModal" class="btn-secondary">Cancel</button>
          <button @click="updateProduct" :disabled="editingProduct" class="btn-primary">
            <div v-if="editingProduct" class="loading-spinner-small"></div>
            {{ editingProduct ? 'Updating...' : 'Update Product/Service' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete All Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Delete All Products/Services</h3>
          <button @click="closeDeleteModal" class="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="warning-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2M12,7A2,2 0 0,0 10,9A2,2 0 0,0 12,11A2,2 0 0,0 14,9A2,2 0 0,0 12,7Z"/>
            </svg>
          </div>
          <h4>Are you sure you want to delete all products and services?</h4>
          <p>This action cannot be undone. All {{ products.length }} item{{ products.length === 1 ? '' : 's' }} will be permanently deleted.</p>
          <div v-if="deleteError" class="error-message">
            {{ deleteError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDeleteModal" class="btn-secondary">Cancel</button>
          <button @click="deleteAllData" :disabled="deleting" class="btn-danger">
            <div v-if="deleting" class="loading-spinner-small"></div>
            {{ deleting ? 'Deleting...' : 'Delete All' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Notification -->
    <div v-if="showSuccessNotification" class="notification success">
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
        </svg>
        <span>{{ successMessage }}</span>
      </div>
    </div>

    <!-- Error Notification -->
    <div v-if="showErrorNotification" class="notification error">
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"/>
        </svg>
        <span>{{ errorMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { DashboardService } from '@/services/dashboard'
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

export default {
  name: 'ProductsServicesView',
  components: {
    Sidebar,
    TopHeader
  },

  data () {
    return {
      loading: false,
      error: null,
      products: [],
      headers: [],
      currentPage: 1,
      itemsPerPage: 6, // Reduced for better card display
      searchQuery: '',
      viewMode: 'cards', // 'cards' or 'list'
      expandedItems: [], // For list view expansion

      // Modal states
      showUploadModal: false,
      showDeleteModal: false,
      showAddSingleModal: false,
      showDeleteSingleModal: false,
      showEditModal: false,
      showAIEnhanceModal: false,

      // Upload functionality
      selectedFile: null,
      uploading: false,
      uploadError: null,

      // Delete functionality
      deleting: false,
      deleteError: null,
      deletingSingle: false,
      deleteSingleError: null,
      productToDelete: null,

      // Add single product functionality
      addingSingle: false,
      addSingleError: null,
      newProduct: {
        name: '',
        description: ''
      },
      addErrors: {},

      // Edit product functionality
      editingProduct: false,
      editError: null,
      productToEdit: null,
      editProduct: {
        name: '',
        description: ''
      },
      editErrors: {},

      // AI Enhancement functionality
      aiProductToEnhance: null,
      enhancingAI: false,
      aiEnhanceError: null,
      aiEnhancedDescription: null,
      originalDescription: null,
      selectedStyle: null,
      creditsUsed: 0,
      remainingCredits: 0,
      updatingProduct: false,

      // Notifications
      showSuccessNotification: false,
      showErrorNotification: false,
      successMessage: '',
      errorMessage: '',
      notificationTimeout: null
    }
  },

  computed: {
    filteredProducts () {
      if (!this.searchQuery) return this.products

      const query = this.searchQuery.toLowerCase()
      return this.products.filter(product => {
        return this.headers.some(header => {
          const value = product[header]
          return value && value.toLowerCase().includes(query)
        })
      })
    },

    totalFilteredPages () {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage)
    },

    paginatedFilteredProducts () {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredProducts.slice(start, end)
    },

    enhancedDescriptionDirection () {
      if (!this.aiEnhancedDescription) return 'auto'

      // RTL character ranges: Arabic, Hebrew, and other RTL scripts
      const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/

      // Check if the text contains RTL characters
      return rtlChars.test(this.aiEnhancedDescription) ? 'rtl' : 'ltr'
    }
  },

  watch: {
    searchQuery () {
      this.currentPage = 1 // Reset to first page when searching
    },

    viewMode () {
      this.currentPage = 1 // Reset to first page when changing view
    }
  },

  mounted () {
    this.loadData()
  },

  methods: {
    async loadData () {
      try {
        this.loading = true
        this.error = null

        const result = await DashboardService.getProductsServices()

        if (result.success) {
          this.products = result.data || []
          this.headers = result.headers || []
        } else {
          this.error = result.message || 'Failed to load data'
        }
      } catch (error) {
        console.error('Error loading products/services:', error)
        this.error = 'Error loading data: ' + error.message
      } finally {
        this.loading = false
      }
    },

    refreshData () {
      this.loadData()
    },

    // Modal handlers
    closeUploadModal () {
      this.showUploadModal = false
      this.selectedFile = null
      this.uploadError = null
    },

    closeDeleteModal () {
      this.showDeleteModal = false
      this.deleteError = null
    },

    closeAddSingleModal () {
      this.showAddSingleModal = false
      this.addSingleError = null
      this.addErrors = {}
      this.newProduct = {
        name: '',
        description: ''
      }
    },

    async addSingleProduct () {
      try {
        this.addingSingle = true
        this.addSingleError = null
        this.addErrors = {}

        const result = await DashboardService.addSingleProductService(this.newProduct)

        if (result.success) {
          this.displaySuccessNotification(result.message || 'Product/service added successfully')
          this.closeAddSingleModal()
          this.loadData() // Refresh the data
        } else if (result.errors) {
          // Validation errors
          this.addErrors = result.errors
        } else if (result.error === 'limit_exceeded') {
          // Plan limit exceeded
          this.addSingleError = result.message
        } else {
          this.addSingleError = result.message || 'Failed to add product/service'
        }
      } catch (error) {
        console.error('Error adding product/service:', error)
        this.addSingleError = 'Error adding product/service: ' + error.message
      } finally {
        this.addingSingle = false
      }
    },

    confirmDeleteProduct (product) {
      this.productToDelete = product
      this.showDeleteSingleModal = true
    },

    closeDeleteSingleModal () {
      this.showDeleteSingleModal = false
      this.deleteSingleError = null
      this.productToDelete = null
    },

    async deleteSingleProduct () {
      if (!this.productToDelete) return

      try {
        this.deletingSingle = true
        this.deleteSingleError = null

        const productId = this.productToDelete.id || this.productToDelete._id
        const result = await DashboardService.deleteSingleProductService(productId)

        if (result.success) {
          this.displaySuccessNotification(result.message || 'Product/service deleted successfully')
          this.closeDeleteSingleModal()
          this.loadData() // Refresh the data
        } else {
          this.deleteSingleError = result.message || 'Failed to delete product/service'
        }
      } catch (error) {
        console.error('Error deleting product/service:', error)
        this.deleteSingleError = 'Error deleting product/service: ' + error.message
      } finally {
        this.deletingSingle = false
      }
    },

    openEditModal (product) {
      this.productToEdit = product
      this.editProduct = {
        name: this.getProductName(product),
        description: product.description || ''
      }
      this.showEditModal = true
    },

    closeEditModal () {
      this.showEditModal = false
      this.editError = null
      this.editErrors = {}
      this.productToEdit = null
      this.editProduct = {
        name: '',
        description: ''
      }
    },

    async updateProduct () {
      if (!this.productToEdit) return

      try {
        this.editingProduct = true
        this.editError = null
        this.editErrors = {}

        const productId = this.productToEdit.id || this.productToEdit._id
        const result = await DashboardService.updateProductService(productId, this.editProduct.description, this.editProduct.name)

        if (result.success) {
          this.displaySuccessNotification(result.message || 'Product/service updated successfully')
          this.closeEditModal()
          this.loadData() // Refresh the data
        } else if (result.errors) {
          // Validation errors
          this.editErrors = result.errors
        } else {
          this.editError = result.message || 'Failed to update product/service'
        }
      } catch (error) {
        console.error('Error updating product/service:', error)
        this.editError = 'Error updating product/service: ' + error.message
      } finally {
        this.editingProduct = false
      }
    },

    openAIEnhanceModal (product) {
      this.aiProductToEnhance = product
      this.originalDescription = product.description || ''
      this.showAIEnhanceModal = true
    },

    closeAIEnhanceModal () {
      this.showAIEnhanceModal = false
      this.aiProductToEnhance = null
      this.enhancingAI = false
      this.aiEnhanceError = null
      this.aiEnhancedDescription = null
      this.originalDescription = null
      this.selectedStyle = null
      this.creditsUsed = 0
      this.remainingCredits = 0
    },

    async enhanceWithAI (style) {
      try {
        this.enhancingAI = true
        this.aiEnhanceError = null
        this.selectedStyle = style

        const productId = this.aiProductToEnhance.id || this.aiProductToEnhance._id
        const result = await DashboardService.aiEnhanceProductService(productId, style)

        if (result.success) {
          this.aiEnhancedDescription = result.enhanced_description
          this.creditsUsed = result.credits_used
          this.remainingCredits = result.remaining_credits
        } else {
          this.aiEnhanceError = result.message || 'Failed to enhance description'
        }
      } catch (error) {
        console.error('Error enhancing with AI:', error)
        this.aiEnhanceError = 'Error enhancing description: ' + error.message
      } finally {
        this.enhancingAI = false
      }
    },

    async updateProductDescription () {
      try {
        this.updatingProduct = true

        const productId = this.aiProductToEnhance.id || this.aiProductToEnhance._id
        const result = await DashboardService.updateProductService(productId, this.aiEnhancedDescription)

        if (result.success) {
          this.displaySuccessNotification(result.message || 'Description updated successfully')
          this.closeAIEnhanceModal()
          this.loadData() // Refresh the data
        } else {
          this.aiEnhanceError = result.message || 'Failed to update description'
        }
      } catch (error) {
        console.error('Error updating product description:', error)
        this.aiEnhanceError = 'Error updating description: ' + error.message
      } finally {
        this.updatingProduct = false
      }
    },

    // File upload handlers
    handleFileSelect (event) {
      this.selectedFile = event.target.files[0]
      this.uploadError = null
    },

    async uploadFile () {
      if (!this.selectedFile) return

      try {
        this.uploading = true
        this.uploadError = null

        const result = await DashboardService.uploadProductsServices(this.selectedFile)

        if (result.success) {
          this.displaySuccessNotification(result.message || 'File processed successfully')
          this.closeUploadModal()
          this.loadData() // Refresh the data
        } else {
          this.uploadError = result.message || 'Failed to upload file'
        }
      } catch (error) {
        console.error('Error uploading file:', error)
        this.uploadError = 'Error uploading file: ' + error.message
      } finally {
        this.uploading = false
      }
    },

    async deleteAllData () {
      try {
        this.deleting = true
        this.deleteError = null

        const result = await DashboardService.deleteAllProductsServices()

        if (result.success) {
          this.displaySuccessNotification(result.message || 'All data deleted successfully')
          this.closeDeleteModal()
          this.products = [] // Clear the local data
          this.headers = []
        } else {
          this.deleteError = result.message || 'Failed to delete data'
        }
      } catch (error) {
        console.error('Error deleting data:', error)
        this.deleteError = 'Error deleting data: ' + error.message
      } finally {
        this.deleting = false
      }
    },

    // Notification handlers
    displaySuccessNotification (message) {
      this.hideNotification()
      this.successMessage = message
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 4000)
    },

    displayErrorNotification (message) {
      this.hideNotification()
      this.errorMessage = message
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 5000)
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    },

    formatHeader (header) {
      // Convert header to a more readable format
      return header
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    formatDate (dateString) {
      if (!dateString) return '-'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    formatContent (content) {
      if (!content) return '-'

      // Convert URLs to clickable links
      const urlRegex = /(https?:\/\/[^\s]+)/g
      let formatted = content.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="content-link">$1</a>')

      // Convert line breaks to HTML
      formatted = formatted.replace(/\n/g, '<br>')

      // Handle emoji and special characters properly
      return formatted
    },

    toggleExpand (productId) {
      const index = this.expandedItems.indexOf(productId)
      if (index > -1) {
        this.expandedItems.splice(index, 1)
      } else {
        this.expandedItems.push(productId)
      }
    },

    getProductName (product) {
      // Handle both formats: 'ServiceName' (from Excel upload) and 'name' (from manual add)
      return product.ServiceName || product.name || 'Unnamed Service'
    },

    getProductHeaders (product) {
      // Filter out the name field from headers to avoid duplication
      return this.headers.filter(header => {
        // If it's an Excel upload (has ServiceName), exclude it
        if (header === 'ServiceName' && product.ServiceName) return false
        // If it's manually added (has name), exclude it
        if (header === 'name' && product.name) return false
        return true
      })
    }
  },

  beforeUnmount () {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }
}
</script>

<style scoped>
.products-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.products-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Setup Steps */
.setup-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.step-card {
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  transition: all 0.3s ease;
}

.step-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.step-number {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
  white-space: nowrap;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.step-description {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  transition: color 0.3s ease;
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.action-buttons-group {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.action-buttons-group.full-width {
  width: 100%;
  margin-left: 0;
}

.action-buttons-group.full-width .btn-add {
  flex: 1;
  justify-content: center;
}

.btn-download {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-download:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-add-single,
.btn-add {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-add-single:hover,
.btn-add:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.btn-upload {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: 1px solid #10b981;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-upload:hover {
  background: #059669;
  border-color: #059669;
}

.btn-delete,
.btn-delete-all {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-delete:hover:not(:disabled),
.btn-delete-all:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.btn-delete:disabled,
.btn-delete-all:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: #dc2626;
  text-align: center;
  transition: color 0.3s ease;
}

.error-icon {
  color: #dc2626;
}

.error-state h3 {
  color: #dc2626;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.error-state p {
  margin: 0;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Search Bar */
.search-bar {
  position: relative;
  flex: 1;
  max-width: 500px;
  min-width: 250px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  font-size: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-search {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.3s ease, background-color 0.3s ease;
}

.clear-search:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Content Container */
.content-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* Results Info */
.results-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  font-size: 14px;
  color: var(--text-secondary);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.view-toggle {
  display: flex;
  gap: 4px;
}

.view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.view-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.view-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* Cards View */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  padding: 24px;
}

.product-card {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
}

.product-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: var(--bg-primary);
  padding: 20px;
  border-bottom: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.card-header-content {
  flex: 1;
  min-width: 0;
}

.service-name {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  transition: color 0.3s ease;
}

.upload-date {
  color: var(--text-secondary);
  font-size: 12px;
  transition: color 0.3s ease;
  font-weight: 500;
}

.card-content {
  padding: 20px;
}

.field-group {
  margin-bottom: 20px;
}

.field-group:last-child {
  margin-bottom: 0;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
}

.field-content {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  word-wrap: break-word;
  white-space: pre-wrap;
  transition: color 0.3s ease;
}

/* List View */
.list-view {
  padding: 0;
}

.product-item {
  border-bottom: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.product-item:last-child {
  border-bottom: none;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.3);
}

.ai-enhance-btn:hover {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(251, 191, 36, 0.4);
}

.ai-enhance-btn svg {
  flex-shrink: 0;
}

.ai-enhance-btn span {
  white-space: nowrap;
}

.edit-product-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  flex-shrink: 0;
}

.edit-product-btn:hover {
  background: #dbeafe;
  color: #3b82f6;
}

.delete-product-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  flex-shrink: 0;
}

.delete-product-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  transition: background-color 0.2s;
}

.item-title {
  flex: 1;
  cursor: pointer;
}

.item-title:hover {
  opacity: 0.8;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-title h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.expand-icon {
  color: var(--text-secondary);
  transition: transform 0.2s, color 0.3s ease;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

.item-content {
  padding: 0 24px 20px 24px;
  border-top: 1px solid var(--border-secondary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* Content Links */
.content-link {
  color: #3b82f6;
  text-decoration: underline;
  word-break: break-all;
}

.content-link:hover {
  color: #2563eb;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  transition: background-color 0.3s ease;
}

.modal-small {
  max-width: 400px;
}

.modal-large {
  max-width: 700px;
}

.text-muted {
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 8px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 0 24px 20px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 0 24px 24px;
}

/* Form Styles */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.input-error,
.form-textarea.input-error {
  border-color: #ef4444;
}

.form-input.input-error:focus,
.form-textarea.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.field-error {
  display: block;
  color: #ef4444;
  font-size: 13px;
  margin-top: 6px;
  font-weight: 500;
}

/* Upload Area */
.upload-area {
  border: 2px dashed var(--border-secondary);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.2s;
  margin: 16px 0;
}

.upload-area:hover {
  border-color: #3b82f6;
  background: var(--bg-secondary);
}

.upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.upload-label svg {
  color: var(--text-secondary);
  transition: color 0.3s ease;
  opacity: 0.7;
}

/* Warning Icon */
.warning-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.warning-icon svg {
  color: #f59e0b;
}

.modal-body h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
  transition: color 0.3s ease;
}

.modal-body p {
  margin: 0 0 16px 0;
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.5;
  transition: color 0.3s ease;
}

/* Error Message */
.error-message {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #991b1b;
  font-size: 14px;
  font-weight: 500;
}

/* Button Variants */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-primary);
}

.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #dc2626;
  color: white;
  border: 1px solid #dc2626;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading Spinner */
.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* AI Enhancement Modal Styles */
.modal-description {
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
  transition: color 0.3s ease;
}

.original-description-box {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.original-description-text {
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  transition: color 0.3s ease;
}

.style-selection {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.style-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.style-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.style-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.style-btn.professional {
  border-color: #3b82f6;
  color: #3b82f6;
}

.style-btn.professional:hover:not(:disabled) {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-color: #2563eb;
}

.style-btn.professional.selected {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #2563eb;
}

.style-btn.cool {
  border-color: #8b5cf6;
  color: #8b5cf6;
}

.style-btn.cool:hover:not(:disabled) {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border-color: #7c3aed;
}

.style-btn.cool.selected {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-color: #7c3aed;
}

.loading-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-message p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.enhancement-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.style-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
}

.style-badge.professional {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.style-badge.cool {
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #6b21a8;
}

.credits-info {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.enhanced-textarea {
  min-height: 200px;
  font-family: inherit;
}

.comparison-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-secondary);
  transition: border-color 0.3s ease;
}

.comparison-section .field-label {
  margin-bottom: 12px;
}

.start-enhance-section {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.btn-start-enhance {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.btn-start-enhance:hover {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(251, 191, 36, 0.5);
}

.btn-start-enhance svg {
  flex-shrink: 0;
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.2) rotate(180deg);
  }
}

.start-enhance-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.credits-cost {
  font-size: 12px;
  font-weight: 300;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Notifications */
.notification {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  max-width: 400px;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

.notification.success {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.notification.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 500;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 32px;
  text-align: center;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.empty-icon {
  color: var(--text-secondary);
  opacity: 0.5;
  transition: color 0.3s ease;
}

.empty-state h3 {
  color: var(--text-primary);
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  transition: color 0.3s ease;
}

.empty-state p {
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: between;
  gap: 16px;
  padding: 16px 20px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.pagination-btn {
  padding: 8px 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-secondary);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-primary);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  flex: 1;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0 !important;
  }
}

@media (max-width: 768px) {
  .products-layout {
    background: var(--bg-secondary);
    overflow-x: hidden;
  }
  .main-content {
    margin-left: 0 !important;
    width: 100%;
    max-width: 100vw;
    min-height: 100vh;
    overflow-x: hidden;
  }
  .page-content {
    padding: 12px;
    padding-top: 76px;
    width: 100%;
    box-sizing: border-box;
  }

  .products-container {
    max-width: 100%;
    width: 100%;
  }

  .setup-steps {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .step-card {
    padding: 20px;
  }

  .step-number {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .step-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 13px;
  }

  .action-bar {
    flex-direction: column;
    gap: 12px;
  }

  .search-bar {
    max-width: 100%;
    width: 100%;
  }

  .action-buttons-group {
    width: 100%;
    margin-left: 0;
  }

  .btn-add, .btn-delete-all, .btn-add-single, .btn-download, .btn-upload, .btn-delete {
    flex: 1;
    justify-content: center;
  }

  .results-info {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }

  .product-card {
    margin: 0;
  }

  .card-header, .card-content {
    padding: 16px;
  }

  .item-header {
    padding: 16px;
  }

  .item-content {
    padding: 0 16px 16px 16px;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-info {
    order: -1;
  }
}

@media (max-width: 480px) {
  .page-content {
    padding: 8px;
    padding-top: 76px;
  }

  .action-buttons-group {
    gap: 8px;
  }

  .cards-grid {
    padding: 12px;
    gap: 12px;
  }

  .service-name {
    font-size: 16px;
  }

  .field-content {
    font-size: 13px;
  }

  .search-input {
    font-size: 16px; /* Prevent zoom on iOS */
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
  }

  .card-actions {
    width: 100%;
    justify-content: space-between;
  }

  .ai-enhance-btn {
    font-size: 12px;
    padding: 6px 12px;
    gap: 6px;
  }

  .ai-enhance-btn svg {
    width: 14px;
    height: 14px;
  }
}
</style>
